import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
	type PhoneInputControllerOptions,
	type PhoneInputViewModel,
} from '#app/components/ui/phone-input.types.ts'
import { SUPPORTED_COUNTRIES, type CountryData } from '#app/utils/countries.ts'
import {
	DEFAULT_COUNTRY_CODE,
	extractLocalPhoneDigits,
	formatPhoneLocal,
	resolveCountryByCode,
	sanitizePhoneDigits,
	toCountryCode,
} from '#app/utils/phone.ts'
import { type CountryCode } from '#types/country.ts'

const COUNTRIES_BY_DIAL_CODE = [...SUPPORTED_COUNTRIES].sort(
	(a, b) => b.dialCode.length - a.dialCode.length,
)

const detectCountryFromValue = (value: string): CountryCode | null => {
	if (!value) return null
	for (const option of COUNTRIES_BY_DIAL_CODE) {
		if (value.startsWith(option.dialCode)) {
			const code = toCountryCode(option.code)
			if (code) return code
		}
	}
	return null
}

const deriveInitialCountryCode = ({
	value,
	explicitCountry,
	defaultCountry,
	autoDetectCountry,
}: {
	value: string
	explicitCountry?: CountryCode | null
	defaultCountry?: CountryCode
	autoDetectCountry: boolean
}): CountryCode => {
	if (explicitCountry) return explicitCountry
	if (autoDetectCountry) {
		const detected = detectCountryFromValue(value)
		if (detected) return detected
	}
	if (defaultCountry) {
		const asCode = toCountryCode(defaultCountry)
		if (asCode) return asCode
	}
	return DEFAULT_COUNTRY_CODE
}

const buildFullNumber = (digits: string, country: CountryData): string => {
	if (!digits) return ''
	return `${country.dialCode}${digits}`
}

export function usePhoneInputController({
	value,
	onChange,
	countryCode,
	onCountryChange,
	disabled = false,
	autoDetectCountry = true,
	defaultCountry,
	extValue,
	onExtChange,
}: PhoneInputControllerOptions): PhoneInputViewModel {
	const initialExplicitCode = toCountryCode(countryCode)
	const [code, setCode] = useState<CountryCode>(() =>
		deriveInitialCountryCode({
			value,
			explicitCountry: initialExplicitCode,
			defaultCountry,
			autoDetectCountry,
		}),
	)

	const countryRef = useRef<CountryCode>(code)
	useEffect(() => {
		countryRef.current = code
	}, [code])

	useEffect(() => {
		const explicit = toCountryCode(countryCode)
		if (explicit && explicit !== code) {
			setCode(explicit)
		}
	}, [countryCode, code])

	const country = useMemo(() => resolveCountryByCode(code), [code])

	const [digits, setDigits] = useState<string>(() =>
		extractLocalPhoneDigits(value, country),
	)
	const digitsRef = useRef(digits)
	useEffect(() => {
		digitsRef.current = digits
	}, [digits])

	useEffect(() => {
		const nextCountry = resolveCountryByCode(code)
		const nextDigits = extractLocalPhoneDigits(value, nextCountry)
		setDigits((current) => (current === nextDigits ? current : nextDigits))
	}, [value, code])

	useEffect(() => {
		if (!autoDetectCountry) return
		if (countryCode) return
		const detected = detectCountryFromValue(value)
		if (!detected) return
		setCode((current) => (current === detected ? current : detected))
	}, [value, autoDetectCountry, countryCode])

	const displayValue = useMemo(
		() => formatPhoneLocal(digits, code),
		[digits, code],
	)

	const emitChange = useCallback(
		(nextDigits: string, nextCountry: CountryData) => {
			const full = buildFullNumber(nextDigits, nextCountry)
			onChange(full)
		},
		[onChange],
	)

	const handleLocalChange = useCallback(
		(next: string) => {
			const nextDigits = sanitizePhoneDigits(next)
			setDigits(nextDigits)
			const nextCountry = resolveCountryByCode(countryRef.current)
			emitChange(nextDigits, nextCountry)
		},
		[emitChange],
	)

	const handleCountryChange = useCallback(
		(next: string) => {
			const nextCode = toCountryCode(next)
			if (!nextCode) return
			setCode(nextCode)
			onCountryChange?.(nextCode)
			const nextCountry = resolveCountryByCode(nextCode)
			const nextDigits = digitsRef.current
			emitChange(nextDigits, nextCountry)
		},
		[emitChange, onCountryChange],
	)

	const reset = useCallback(
		(nextValue: string, nextCountry?: CountryCode | null) => {
			const explicit = toCountryCode(nextCountry ?? countryRef.current)
			const finalCode = explicit ?? DEFAULT_COUNTRY_CODE
			setCode(finalCode)
			const finalCountry = resolveCountryByCode(finalCode)
			const nextDigits = extractLocalPhoneDigits(nextValue, finalCountry)
			setDigits(nextDigits)
		},
		[],
	)

	const extensionProps =
		onExtChange != null
			? {
					value: extValue ?? '',
					onChange: onExtChange,
					disabled,
				}
			: null

	return {
		country,
		countryCode: code,
		dialCode: country.dialCode,
		digits,
		displayValue,
		selectProps: {
			value: code,
			onValueChange: handleCountryChange,
			disabled,
		},
		inputProps: {
			value: displayValue,
			onChange: handleLocalChange,
			disabled,
		},
		extensionProps,
		reset,
	}
}
